import React, { useLayoutEffect, useCallback } from 'react';
import { useLocation, useParams } from 'react-router';
import queryString from 'query-string';
import { NavLink } from 'react-router-dom';

import Info from 'Shared/components/Info/Info';

import { CreateAccountFlowType } from '../types';
import GoogleButton from './GoogleButton/GoogleButton';
import Office365Button from './Office365Button/Office365Button';
import CreateAccountForm from './CreateAccountForm/CreateAccountForm';
import { WpButtonGroup } from 'Wpds/components/button';
import SignUpTermsAgreement from 'Shared/components/SignUpTermsAgreement/SignUpTermsAgreement';

import './CreateAccountModalFormArea.scss';

const defaultTitle = 'Almost there!';
const defaultDescription = "Sign up now to save all of the workspaces you've created";

// Hooks
import { useApp } from 'Shared/utils/hooks/use-app';
import { useCurrentUser } from 'Shared/utils/hooks/use-current-user';

type Props = {
  title?: string;
  description?: string;
  createAccountFlowType?: CreateAccountFlowType;
  showOAuthButtons?: boolean;
  cannotSignIntoGoogle?: boolean;
};

function getSignInUrl({ createAccountFlowType, inviterSlug, signInUrl }) {
  switch (createAccountFlowType) {
    case CreateAccountFlowType.signUpByInvitationLink:
      return `${signInUrl}?inviter_slug=${inviterSlug}`;
    case CreateAccountFlowType.acceptInviteFromEmail:
      return signInUrl;
    default:
      return null;
  }
}

function CreateAccountModalFormArea({
  title = defaultTitle,
  description = defaultDescription,
  createAccountFlowType = CreateAccountFlowType.signUp,
  showOAuthButtons = true,
  cannotSignIntoGoogle = false,
}: Props) {
  const { search } = useLocation();
  const { slug: inviterSlug } = useParams();
  const query = queryString.parse(search);
  const workspaceSlugFromUrl = query?.workspace_slug ? String(query?.workspace_slug) : null;
  const invitationTokenFromUrl = query?.invitation_token ? String(query?.invitation_token) : null;
  const {
    app: {
      invitationToken: invitationTokenFromApp,
      routes: { signUpUrl, signInUrl, googleSignUpUrl, office365SignUpUrl, userInvitePath },
    },
  } = useApp();

  const { user } = useCurrentUser();
  const userEmail = user.email;

  const signInRedirectUrl = getSignInUrl({ createAccountFlowType, inviterSlug, signInUrl });

  // During initial load we get the token through the query params, if we reload the page
  // because of a form validation error then we must pass it in through the app state.
  const invitationToken = invitationTokenFromUrl || invitationTokenFromApp;

  const getPropertiesForCreateAccountForm = useCallback(() => {
    switch (createAccountFlowType) {
      case CreateAccountFlowType.signUp:
        return {
          actionPath: signUpUrl,
          showBackButton: true,
        };
      case CreateAccountFlowType.signUpByInvitationLink:
        return {
          actionPath: signUpUrl,
          inviterSlug,
          workspaceSlug: workspaceSlugFromUrl,
        };
      case CreateAccountFlowType.acceptInviteFromEmail:
        return {
          actionPath: userInvitePath,
          currentEmail: userEmail,
          invitationToken,
        };
      default:
        return {
          actionPath: signUpUrl,
        };
    }
  }, [createAccountFlowType, inviterSlug, workspaceSlugFromUrl, signUpUrl, userInvitePath, userEmail, invitationToken]);

  useLayoutEffect(() => {
    const flashAlertBlock = document.getElementById('alertBlock');
    if (flashAlertBlock) flashAlertBlock.style.display = 'none';
  }, []);

  const workEmailError = search.includes('work_email_error');

  const additionalQueryParams = `${inviterSlug ? `&inviter_slug=${inviterSlug}` : ''}${
    workspaceSlugFromUrl ? `&workspace_slug=${workspaceSlugFromUrl}` : ''
  }${invitationToken ? `&invitation_token=${invitationToken}` : ''}`;

  return (
    <div className="create-account-form-area pl-lg-4 pb-lg-4 pr-lg-5">
      {workEmailError && (
        <Info type="danger" className="mb-3">
          Please use your work email to sign up.
        </Info>
      )}
      <div className="create-account-form-area-title mt-lg-4">{title}</div>
      <div className="create-account-form-area-description mt-3 pb-2">{description}</div>
      {showOAuthButtons && (
        <>
          <div className="d-flex flex-column mb-4">
            <WpButtonGroup stacked>
              {!cannotSignIntoGoogle && (
                <GoogleButton variant="secondary" googleSignUpUrl={`${googleSignUpUrl}${additionalQueryParams || ''}`}>
                  Sign up with Google
                </GoogleButton>
              )}
              <Office365Button
                variant="secondary"
                office365SignUpUrl={`${office365SignUpUrl}${additionalQueryParams || ''}`}
              >
                Sign up with Office 365
              </Office365Button>
            </WpButtonGroup>
          </div>
          <div className="create-account-form-area-description d-flex align-items-center justify-content-center w-100">
            <hr className="w-100 mr-3" />
            or
            <hr className="w-100 ml-3" />
          </div>
        </>
      )}
      <CreateAccountForm {...getPropertiesForCreateAccountForm()} />
      <SignUpTermsAgreement className="create-account-form-area-terms mt-4 pt-2" />
      {signInRedirectUrl && (
        <div className="create-account-form-area-terms mt-4">
          Already have an account?
          <NavLink to={signInRedirectUrl} className="ml-1">
            Sign in
          </NavLink>
        </div>
      )}
    </div>
  );
}

export default CreateAccountModalFormArea;
